import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer'
import moment from 'moment'

// The e-learning completion certificate. Page 1 (A4 landscape) is the
// certificate; page 2 (A4 portrait) is the learner's reflective record and
// is left out when there are no reflections.
//
// Self-contained on purpose: no app contexts, no asset imports. The CPD
// submission pack script renders it in Node with a placeholder learner.
//
// Helvetica is built into PDF but has no "greater/less than or equal"
// glyphs - do not use them in any text here.

const INK = '#1A2230'
const MUTED = '#4B5563'
const RULE = '#239AA4'
const WARN = '#B42318'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    color: INK,
    fontSize: 10.5,
    paddingTop: 40,
    paddingBottom: 46,
    paddingHorizontal: 48,
  },
  frame: {
    position: 'absolute',
    top: 18,
    left: 18,
    right: 18,
    bottom: 18,
    borderWidth: 2,
    borderColor: RULE,
  },
  topRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', minHeight: 44 },
  cpdBlock: { maxWidth: 260, alignItems: 'flex-end' },
  cpdTitle: { fontFamily: 'Helvetica-Bold', fontSize: 11 },
  title: { fontFamily: 'Helvetica-Bold', fontSize: 30, marginTop: 10 },
  lead: { fontSize: 11, color: MUTED, marginTop: 10 },
  learner: { fontFamily: 'Helvetica-Bold', fontSize: 22, marginTop: 3 },
  course: { fontFamily: 'Helvetica-Bold', fontSize: 18, marginTop: 3 },
  pathway: { fontSize: 11.5, marginTop: 4 },
  columns: { flexDirection: 'row', marginTop: 14, gap: 24 },
  details: { flexGrow: 1, flexBasis: 0 },
  detailRow: { flexDirection: 'row', paddingVertical: 2.5, borderBottomWidth: 0.5, borderBottomColor: '#CBD5E1' },
  detailLabel: { width: 150, color: MUTED },
  detailValue: { flexGrow: 1, flexBasis: 0, fontFamily: 'Helvetica-Bold' },
  verify: { width: 250, flexDirection: 'row', gap: 10 },
  qr: { width: 84, height: 84 },
  verifyText: { flexGrow: 1, flexBasis: 0, fontSize: 8.5 },
  verifyId: { fontFamily: 'Courier-Bold', fontSize: 9, marginTop: 2, marginBottom: 4 },
  verifyUrl: { marginTop: 8, fontSize: 9.5 },
  statement: { marginTop: 10, fontSize: 9.5, lineHeight: 1.35 },
  practicalItem: { flexDirection: 'row', fontSize: 9, marginTop: 2, paddingLeft: 6 },
  footer: {
    position: 'absolute',
    left: 48,
    right: 48,
    bottom: 26,
    fontSize: 8,
    color: MUTED,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  watermark: {
    position: 'absolute',
    top: '44%',
    left: 0,
    right: 0,
    textAlign: 'center',
    fontFamily: 'Helvetica-Bold',
    fontSize: 38,
    color: WARN,
    opacity: 0.22,
    transform: 'rotate(-24deg)',
  },
  banner: {
    position: 'absolute',
    top: 18,
    left: 18,
    right: 18,
    backgroundColor: WARN,
    color: '#FFFFFF',
    fontFamily: 'Helvetica-Bold',
    fontSize: 12,
    textAlign: 'center',
    paddingVertical: 4,
  },
  // Reflective record
  recordPage: {
    fontFamily: 'Helvetica',
    color: INK,
    fontSize: 11,
    lineHeight: 1.45,
    paddingTop: 48,
    paddingBottom: 60,
    paddingHorizontal: 48,
  },
  recordTitle: { fontFamily: 'Helvetica-Bold', fontSize: 20, lineHeight: 1.2 },
  recordMeta: { color: MUTED, marginTop: 4 },
  prompt: { fontFamily: 'Helvetica-Bold', marginTop: 16, marginBottom: 4 },
  paragraph: { marginBottom: 6 },
})

// Verification addresses and the watermark must never be split with a hyphen.
const noHyphenation = (word) => [word]

const formatDate = (value) => (value ? moment(value).format('D MMMM YYYY') : '')

const DetailRow = ({ label, value }) => (
  <View style={styles.detailRow}>
    <Text style={styles.detailLabel}>{label}</Text>
    <Text style={styles.detailValue}>{value}</Text>
  </View>
)

// Sample and withdrawn markings, repeated on every page.
const Markings = ({ isSample, revoked, fontSize = 38 }) => (
  <>
    {isSample ? (
      <Text style={[styles.watermark, { fontSize }]} hyphenationCallback={noHyphenation} fixed>
        SAMPLE - NOT A VALID CERTIFICATE
      </Text>
    ) : null}
    {revoked ? (
      <Text style={styles.banner} fixed>
        WITHDRAWN
      </Text>
    ) : null}
  </>
)

const CertificatePDF = ({ certificate, qrDataUrl }) => {
  const snapshot = certificate?.snapshot ?? {}
  const accreditation = snapshot.accreditation ?? {}
  // The only switch for any CPD wording on this document.
  const accredited = accreditation.status === 'accredited'
  const practicalOutcomes = snapshot.practicalOutcomes ?? []
  const reflections = (snapshot.reflections ?? []).filter((reflection) => reflection?.text)
  const level = [snapshot.pathwayTitle, snapshot.cstfLevel].filter(Boolean).join(' - ')
  const hasScore = snapshot.score != null && snapshot.total != null
  const revoked = !!certificate?.revokedAt

  return (
    <Document
      title={`Certificate of Completion - ${snapshot.courseTitle ?? ''}`}
      author="AmbuNet (Care Nav Ltd)"
      subject={snapshot.courseCode ?? ''}
      language="en-GB"
    >
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.frame} fixed />
        <Markings isSample={certificate?.isSample} revoked={revoked} />

        <View style={styles.topRow}>
          {/* No organisation logo: the course is developed and certified by
              AmbuNet. The learner's organisation is named in the details. */}
          <View />
          {accredited ? (
            <View style={styles.cpdBlock}>
              {/* CPD LOGO PLACEHOLDER: when we hold the CPD Certified artwork,
                  add <Image src={cpdLogo} style={{ height: 44 }} /> here. It
                  must stay inside this `accredited` branch: the mark may only
                  appear for a course version CPD UK has certified. */}
              <Text style={styles.cpdTitle}>CPD Certified by The CPD Certification Service</Text>
              {accreditation.cpdReference ? <Text>CPD reference: {accreditation.cpdReference}</Text> : null}
            </View>
          ) : null}
        </View>

        <Text style={styles.title}>Certificate of Completion</Text>

        <Text style={styles.lead}>This certifies that</Text>
        <Text style={styles.learner}>{snapshot.learnerName}</Text>
        <Text style={styles.lead}>has completed</Text>
        <Text style={styles.course}>
          {snapshot.courseTitle}
          {snapshot.courseCode ? ` (${snapshot.courseCode})` : ''}
        </Text>
        {level ? <Text style={styles.pathway}>{level}</Text> : null}

        <View style={styles.columns}>
          <View style={styles.details}>
            {snapshot.organisation ? <DetailRow label="Learner's organisation" value={snapshot.organisation} /> : null}
            <DetailRow label="Course developed by" value="AmbuNet (Care Nav Ltd)" />
            <DetailRow label="Course version" value={snapshot.courseVersion} />
            <DetailRow label="Completed on" value={formatDate(snapshot.completedOn)} />
            <DetailRow label="Valid until" value={formatDate(snapshot.expiresOn) || 'No expiry date'} />
            {hasScore ? (
              <DetailRow
                label="Assessment result"
                value={`${snapshot.score}/${snapshot.total} (${snapshot.percentage}%)`}
              />
            ) : null}
            {snapshot.estimatedMinutes ? (
              <DetailRow label="Estimated learning time" value={`${snapshot.estimatedMinutes} minutes`} />
            ) : null}
            {accredited && accreditation.cpdHours ? (
              <DetailRow label="CPD hours" value={String(accreditation.cpdHours)} />
            ) : null}
          </View>

          <View style={styles.verify}>
            {qrDataUrl ? <Image src={qrDataUrl} style={styles.qr} /> : null}
            <View style={styles.verifyText}>
              <Text>Verification ID</Text>
              <Text style={styles.verifyId} hyphenationCallback={noHyphenation}>
                {certificate?.verificationId}
              </Text>
              <Text>Scan the code, or use the address below, to check this certificate.</Text>
            </View>
          </View>
        </View>

        {certificate?.verifyUrl ? (
          <Text style={styles.verifyUrl} hyphenationCallback={noHyphenation}>
            Verify this certificate at {certificate.verifyUrl}
          </Text>
        ) : null}

        {snapshot.frameworkStatement ? <Text style={styles.statement}>{snapshot.frameworkStatement}</Text> : null}
        {snapshot.certificateNote ? <Text style={styles.statement}>{snapshot.certificateNote}</Text> : null}
        {practicalOutcomes.length ? (
          <View style={{ marginTop: 10 }}>
            <Text style={{ fontSize: 9.5, lineHeight: 1.35 }}>
              Practical competence for the following outcomes is signed off separately by the learner's organisation
              and is not evidenced by this certificate:
            </Text>
            {practicalOutcomes.map((outcome) => (
              <View key={outcome.id} style={styles.practicalItem}>
                <Text style={{ width: 10 }}>-</Text>
                <Text style={{ flexGrow: 1, flexBasis: 0 }}>{outcome.text || outcome.id}</Text>
              </View>
            ))}
          </View>
        ) : null}

        <View style={styles.footer} fixed>
          <Text>Course developed and provided by AmbuNet (Care Nav Ltd)</Text>
          <Text>Verification ID {certificate?.verificationId}</Text>
        </View>
      </Page>

      {reflections.length ? (
        <Page size="A4" style={styles.recordPage}>
          <Markings isSample={certificate?.isSample} revoked={revoked} fontSize={26} />
          <Text style={styles.recordTitle}>Reflective record</Text>
          <Text style={styles.recordMeta}>
            {snapshot.learnerName} - {snapshot.courseTitle}
            {snapshot.courseCode ? ` (${snapshot.courseCode})` : ''}
          </Text>
          <Text style={styles.recordMeta}>
            The learner's own reflections, written during the course
            {snapshot.completedOn ? ` and completed on ${formatDate(snapshot.completedOn)}` : ''}.
          </Text>

          {reflections.map((reflection, index) => (
            // Blocks are free to break across pages: a long reflection can be
            // taller than a page. Only the prompt is kept with its first lines.
            // eslint-disable-next-line react/no-array-index-key
            <View key={index}>
              <Text style={styles.prompt} minPresenceAhead={40}>
                {reflection.prompt}
              </Text>
              {String(reflection.text)
                .split(/\n+/)
                .filter((paragraph) => paragraph.trim())
                .map((paragraph, paragraphIndex) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Text key={paragraphIndex} style={styles.paragraph}>
                    {paragraph}
                  </Text>
                ))}
            </View>
          ))}

          <View style={styles.footer} fixed>
            <Text>Course developed and provided by AmbuNet (Care Nav Ltd)</Text>
            <Text
              render={({ pageNumber, totalPages }) =>
                `Verification ID ${certificate?.verificationId ?? ''} - page ${pageNumber} of ${totalPages}`
              }
            />
          </View>
        </Page>
      ) : null}
    </Document>
  )
}

export default CertificatePDF
